/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

// copied from https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v5-hash/src/components/LoginCallback.tsx
import React from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';

import { loginChannel, useLogin } from '../modules/login/useLogin';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'routes' is defined but never used.
import routes from '../routes/routes.constants';

const LoginCallback: React.FC<{
  homePath: string;
  loadingElement: React.ReactElement;
}> = ({ homePath, loadingElement }) => {
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  const { success } = useLogin();

  React.useEffect(() => {
    // TODO: remove this once we have a proper way to handle the login redirect
    // Add timeout to force reload after 2 seconds
    const timeoutId = setTimeout(() => {
      window.location.reload();
    }, 2000);

    const handleLoginRedirect = async () => {
      await oktaAuth.handleLoginRedirect();
    };

    if (oktaAuth.token.isLoginRedirect()) {
      // store redirectUri, it's cleared after `handleLoginRedirect` call
      //const redirectUri = oktaAuth.getOriginalUri();
      handleLoginRedirect()
        .then(() => {
          // copied from `Login`
          // (overlaps redirect functionality from okta source - commented code left as reference)
          const accessToken = oktaAuth.getAccessToken();
          return accessToken
            ? success(accessToken).then(() => {
                loginChannel.postMessage('login');
              })
            : null;
        })
        // .then(() => {
        //   if (!redirectUri) {
        //     // manual redirect when redirectUri is not set
        //     // otherwise allow default behavior
        //     history.replace(homePath);
        //   }
        // })
        .catch((e) => {
          // TODO: handle error state(s)
          console.error(e);
        });
    } else {
      // we landed on root path, but do not have login callback params in url query string
      history.replace(homePath);
    }
    // Clean up timeout on unmount or successful login
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook React.useEffect has a missing dependency: 'success'. Either include it or remove the dependency array.
  }, [oktaAuth, history, homePath]);

  return loadingElement;
};

export default LoginCallback;
